import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useRestoration } from '@/components/layout/RestorationProvider'
import { parseUrlState, serializeUrlState, type Schema, type StateOf } from '@/lib/urlState'

interface SetOptions {
  /** Skip the scroll reset. Used by the corrective mount-time rewrite. */
  silent?: boolean
}

/**
 * The URL query string is the single source of truth for a page's filters.
 *
 * Writes use `replace: true` so a filter change never adds a history entry —
 * back always means "go back a page", not "undo my last checkbox".
 */
export function useUrlState<S extends Schema>(
  schema: S,
): [
  StateOf<S>,
  (patch: Partial<StateOf<S>>, opts?: SetOptions) => void,
  (patch: Partial<StateOf<S>>) => string,
] {
  const [searchParams, setSearchParams] = useSearchParams()
  const { resetScroll } = useRestoration()

  // `setSearchParams` is `navigate('?' + params, opts)` under the hood, so an
  // options object without `state` REPLACES the history entry with a null one —
  // silently dropping the `from` referrer that BackButton and the lightbox's
  // close branch depend on. Carry the current entry's state through every write.
  const { state: locationState } = useLocation()
  const locationStateRef = useRef(locationState)
  locationStateRef.current = locationState

  const state = useMemo(() => parseUrlState(schema, searchParams), [schema, searchParams])

  const setState = useCallback(
    (patch: Partial<StateOf<S>>, opts?: SetOptions) => {
      const next = serializeUrlState(schema, { ...state, ...patch }, searchParams)
      setSearchParams(next, { replace: true, state: locationStateRef.current })
      if (!opts?.silent) resetScroll()
    },
    [schema, state, searchParams, setSearchParams, resetScroll],
  )

  /**
   * The query string that applying `patch` would produce, without navigating.
   * Lets a <Link> drop one filter while preserving every other param.
   */
  const buildSearch = useCallback(
    (patch: Partial<StateOf<S>>) => {
      const s = serializeUrlState(schema, { ...state, ...patch }, searchParams).toString()
      return s ? `?${s}` : ''
    },
    [schema, state, searchParams],
  )

  // One corrective rewrite on mount if the URL carried malformed or defaulted
  // params (e.g. ?online=banana, ?group=all). Silent, so it cannot clobber a
  // POP restore that is waiting on the gate.
  const corrected = useRef(false)
  useEffect(() => {
    if (corrected.current) return
    corrected.current = true
    const canonical = serializeUrlState(schema, state, searchParams)
    if (canonical.toString() !== searchParams.toString()) {
      setSearchParams(canonical, { replace: true, state: locationStateRef.current })
    }
    // Mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return [state, setState, buildSearch]
}
